function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function stableStringify(value) {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  }

  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }

  return JSON.stringify(value);
}

function hashStringToHex(value) {
  let hash = 2166136261;
  const input = String(value || "");

  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0).toString(16).padStart(8, "0");
}

function buildSourceFingerprint(payload) {
  return hashStringToHex(stableStringify(payload));
}

function toIsoString(value) {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function getLatestUpdatedAt(docs) {
  const timestamps = docs
    .map((doc) => new Date(doc.updatedAt || 0))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => b.getTime() - a.getTime());

  return timestamps[0] ? timestamps[0].toISOString() : null;
}

function buildReportId(organizationName) {
  return `seec-exception-report-${slugify(organizationName)}`;
}

function normalizeText(value) {
  return String(value || "").trim();
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isItemizedIndividualReceipt(row) {
  return /itemized contributions from individuals/i.test(
    String(row?.receiptType || ""),
  );
}

function flattenReceiptRows(receiptDocs) {
  return receiptDocs.flatMap((doc) =>
    (doc.data || []).map((row, index) => ({
      ...row,
      __docId: doc._id || doc.id || null,
      __sourceYear: doc.year || null,
      __organizationName: doc.organizationName || null,
      __townName: doc.townName || null,
      __townSlug: doc.townSlug || null,
      __rowIndex: index,
    })),
  );
}

function flattenDisbursementRows(disbursementDocs) {
  return disbursementDocs.flatMap((doc) =>
    (doc.data || []).map((row, index) => ({
      ...row,
      __docId: doc._id || doc.id || null,
      __sourceYear: doc.year || null,
      __organizationName: doc.organizationName || null,
      __townName: doc.townName || null,
      __townSlug: doc.townSlug || null,
      __rowIndex: index,
    })),
  );
}

function flattenSummaryRows(summaryDocs) {
  return summaryDocs.flatMap((doc) =>
    (doc.data || []).map((row, index) => ({
      ...row,
      __docId: doc._id || doc.id || null,
      __sourceYear: doc.year || null,
      __organizationName: doc.organizationName || null,
      __townName: doc.townName || null,
      __townSlug: doc.townSlug || null,
      __rowIndex: index,
    })),
  );
}

function buildFindingBase({
  id,
  lawId,
  severity,
  summary,
  details,
  sourceType,
  sourceYear,
  filingEventType = null,
  sourceRowId = null,
  pdfUrl = null,
  confidence = "high",
  detectionMethod = "deterministic",
}) {
  return {
    id,
    lawId,
    severity,
    summary,
    details,
    sourceType,
    sourceYear,
    filingEventType,
    sourceRowId,
    pdfUrl,
    confidence,
    detectionMethod,
    status: "potential",
  };
}

function detectReceiptFindings(receiptDocs) {
  const rows = flattenReceiptRows(receiptDocs);
  const findings = [];

  for (const row of rows) {
    if (!isItemizedIndividualReceipt(row)) continue;

    const contributorName = normalizeText(row.contributorName);
    const streetAddress = normalizeText(row.streetAddress);
    const city = normalizeText(row.city || row.townName);
    const state = normalizeText(row.state);
    const zipCode = normalizeText(row.zipCode);
    const occupation = normalizeText(row.occupation);
    const employer = normalizeText(row.employer);
    const sourceRowId =
      row.receiptId ||
      row.contributionId ||
      `${row.__docId || "receipt"}:${row.__rowIndex}`;

    if (!contributorName) {
      findings.push(
        buildFindingBase({
          id: `missing-itemized-contributor-name-${sourceRowId}`,
          lawId: "SEEC-0105",
          severity: "high",
          summary: "Itemized individual receipt is missing a contributor name.",
          details: `Receipt row ${sourceRowId} for ${row.amountOriginal || row.amount || 0} on ${row.transactionDate || "unknown date"} does not include a contributor name.`,
          sourceType: "SEEC_RECEIPTS",
          sourceYear: row.__sourceYear,
          sourceRowId,
        }),
      );
      continue;
    }

    const missingAddressParts = [];
    if (!streetAddress) missingAddressParts.push("street address");
    if (!city) missingAddressParts.push("city");
    if (!state) missingAddressParts.push("state");
    if (!zipCode) missingAddressParts.push("zip code");

    if (missingAddressParts.length > 0) {
      findings.push(
        buildFindingBase({
          id: `missing-itemized-contributor-address-${sourceRowId}`,
          lawId: "SEEC-0105",
          severity: "high",
          summary: `Itemized receipt for ${contributorName} is missing contributor address information.`,
          details: `Receipt row ${sourceRowId} is missing ${missingAddressParts.join(", ")}.`,
          sourceType: "SEEC_RECEIPTS",
          sourceYear: row.__sourceYear,
          sourceRowId,
        }),
      );
    }

    const missingOccupationEmployerParts = [];
    if (!occupation) missingOccupationEmployerParts.push("occupation");
    if (!employer) missingOccupationEmployerParts.push("employer");

    if (missingOccupationEmployerParts.length > 0) {
      findings.push(
        buildFindingBase({
          id: `missing-occupation-employer-${sourceRowId}`,
          lawId: "SEEC-0105",
          severity: "medium",
          summary: `Itemized receipt for ${contributorName} is missing ${missingOccupationEmployerParts.join(" and ")}.`,
          details: `Receipt row ${sourceRowId} does not contain ${missingOccupationEmployerParts.join(" and ")} information.`,
          sourceType: "SEEC_RECEIPTS",
          sourceYear: row.__sourceYear,
          sourceRowId,
          confidence: "medium",
        }),
      );
    }
  }

  return findings;
}

function detectDisbursementFindings(disbursementDocs) {
  const rows = flattenDisbursementRows(disbursementDocs);
  const findings = [];

  for (const row of rows) {
    const payeeName = normalizeText(row.payeeName);
    const purpose = normalizeText(row.purposeOfExpenditure);
    const description = normalizeText(row.description);
    const sourceRowId =
      row.disbursementId ||
      `${row.__docId || "disbursement"}:${row.__rowIndex}`;

    if (!payeeName) {
      findings.push(
        buildFindingBase({
          id: `missing-payee-${sourceRowId}`,
          lawId: "SEEC-0107",
          severity: "high",
          summary: "Expenditure row is missing a payee name.",
          details: `Disbursement row ${sourceRowId} for ${row.amountOriginal || row.amount || 0} on ${row.dateOfPayment || "unknown date"} does not list a payee.`,
          sourceType: "SEEC_DISBURSEMENTS",
          sourceYear: row.__sourceYear,
          sourceRowId,
        }),
      );
    }

    if (!purpose && !description) {
      findings.push(
        buildFindingBase({
          id: `missing-expenditure-purpose-${sourceRowId}`,
          lawId: "SEEC-0107",
          severity: "high",
          summary: `Expenditure row${payeeName ? ` for ${payeeName}` : ""} is missing both purpose and description.`,
          details: `Disbursement row ${sourceRowId} does not include a purpose of expenditure or supporting description.`,
          sourceType: "SEEC_DISBURSEMENTS",
          sourceYear: row.__sourceYear,
          sourceRowId,
        }),
      );
    }
  }

  return findings;
}

function detectSummaryFindings(summaryDocs) {
  const rows = flattenSummaryRows(summaryDocs).sort((a, b) => {
    const aDate = parseDate(a.periodCovBeginDateIso || a.periodCovBeginDate);
    const bDate = parseDate(b.periodCovBeginDateIso || b.periodCovBeginDate);
    return (aDate?.getTime() || 0) - (bDate?.getTime() || 0);
  });
  const findings = [];

  for (let i = 1; i < rows.length; i += 1) {
    const previous = rows[i - 1];
    const current = rows[i];
    const previousEnding = Number(
      previous.balanceOnHandAtCloseOfReportingPeriod || 0,
    );
    const currentBeginning = Number(
      current.balanceOnHandAtBeginningOfReportingPeriod || 0,
    );
    const diff = Math.abs(previousEnding - currentBeginning);

    if (diff > 0.01) {
      findings.push(
        buildFindingBase({
          id: `summary-balance-rollforward-mismatch-${current.__docId || "summary"}-${current.__rowIndex}`,
          lawId: "SEEC-0108",
          severity: "high",
          summary: "Summary filing balances do not roll forward cleanly between adjacent periods.",
          details: `Previous period ending balance ${previousEnding.toLocaleString()} does not match next beginning balance ${currentBeginning.toLocaleString()} for ${current.filingEventType || "filing"}.`,
          sourceType: "SEEC_SUMMARIES",
          sourceYear: current.__sourceYear,
          filingEventType: current.filingEventType || null,
          sourceRowId: `${current.__docId || "summary"}:${current.__rowIndex}`,
          pdfUrl: current.pdfUrl || null,
        }),
      );
    }
  }

  return findings;
}

function sortFindings(findings) {
  const severityWeight = { high: 0, medium: 1, low: 2 };
  return [...findings].sort((a, b) => {
    const severityDiff =
      (severityWeight[a.severity] ?? 99) - (severityWeight[b.severity] ?? 99);
    if (severityDiff !== 0) return severityDiff;
    return String(a.summary || "").localeCompare(String(b.summary || ""));
  });
}

function buildSeecExceptionReport({
  organizationName,
  townName,
  townSlug,
  receiptsDocs = [],
  disbursementDocs = [],
  summaryDocs = [],
  generatedBy = "system",
  generationMethod = "deterministic",
}) {
  const sourceFingerprint = buildSourceFingerprint({
    receipts: receiptsDocs.map((doc) => ({
      _id: doc._id || doc.id || null,
      updatedAt: doc.updatedAt || null,
      rowCount: doc.rowCount || 0,
    })),
    disbursements: disbursementDocs.map((doc) => ({
      _id: doc._id || doc.id || null,
      updatedAt: doc.updatedAt || null,
      rowCount: doc.rowCount || 0,
    })),
    summaries: summaryDocs.map((doc) => ({
      _id: doc._id || doc.id || null,
      updatedAt: doc.updatedAt || null,
      rowCount: doc.rowCount || 0,
    })),
  });

  const findings = sortFindings([
    ...detectReceiptFindings(receiptsDocs),
    ...detectDisbursementFindings(disbursementDocs),
    ...detectSummaryFindings(summaryDocs),
  ]);

  const generatedAt = new Date().toISOString();

  return {
    _id: buildReportId(organizationName),
    entityType: "seec_exception_report",
    organizationName,
    committeeName: organizationName,
    townName,
    townSlug,
    sourceCollection: "SEEC",
    targetCollection: "SEEC_EXCEPTION_REPORTS",
    sourceFingerprint,
    sourceSummaryUpdatedAt: getLatestUpdatedAt(summaryDocs),
    sourceReceiptsUpdatedAt: getLatestUpdatedAt(receiptsDocs),
    sourceDisbursementsUpdatedAt: getLatestUpdatedAt(disbursementDocs),
    status: findings.length > 0 ? "generated" : "no_findings",
    findings,
    findingCount: findings.length,
    llmSummary: null,
    llmModel: null,
    llmGeneratedAt: null,
    generationMethod,
    generatedAt,
    generatedBy,
    lastCheckedAt: generatedAt,
    meta: {
      scaffoldVersion: 2,
      sourceDocCounts: {
        receipts: receiptsDocs.length,
        disbursements: disbursementDocs.length,
        summaries: summaryDocs.length,
      },
      sourceRowCounts: {
        receipts: receiptsDocs.reduce((sum, doc) => sum + Number(doc.rowCount || 0), 0),
        disbursements: disbursementDocs.reduce((sum, doc) => sum + Number(doc.rowCount || 0), 0),
        summaries: summaryDocs.reduce((sum, doc) => sum + Number(doc.rowCount || 0), 0),
      },
    },
  };
}

module.exports = {
  buildSeecExceptionReport,
  buildSourceFingerprint,
  buildReportId,
};
